/**
 * BulkDeleteBar
 *
 * Shared bar + confirmation dialog used by HQ listing pages (Expenses,
 * Purchase Orders) to let the HQ Super Admin wipe multiple selected
 * rows at once. The bar only renders for HQ Super Admin users.
 *
 * Props:
 *   selectedIds   : string[]        - currently selected row ids
 *   onCleared     : () => void      - called after a successful delete to
 *                                      refresh the parent list
 *   endpoint      : string          - server path (e.g. '/hq/bulk-delete/expenses')
 *   entityLabel   : string          - e.g. 'expense' / 'purchase order'
 *   supportModes  : boolean         - if true, the dialog offers a Soft/Hard
 *                                      cascade choice (used for POs)
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Trash2, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';

export default function BulkDeleteBar({
  selectedIds,
  onCleared,
  endpoint,
  entityLabel,
  supportModes = false,
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuper = user?.role_name === 'HQ Super Admin';
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [mode, setMode] = useState('soft');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setPassword('');
      setShowPw(false);
      setMode('soft');
    }
  }, [open]);

  if (!isSuper || !selectedIds || selectedIds.length === 0) return null;

  const submit = async () => {
    if (!password) return;
    setBusy(true);
    try {
      const body = { ids: selectedIds, password };
      if (supportModes) body.mode = mode;
      const res = await api.post(endpoint, body);
      toast({
        title: 'Deleted',
        description: `${res.data.total_deleted} records removed`,
      });
      setOpen(false);
      onCleared?.();
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 px-3 py-2 bg-rose-50 border border-rose-200 rounded-md">
        <ShieldAlert className="w-4 h-4 text-rose-600" />
        <span className="text-sm text-rose-800 font-medium">
          {selectedIds.length} {entityLabel}{selectedIds.length > 1 ? 's' : ''} selected
        </span>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => setOpen(true)}
          data-testid={`bulk-delete-${entityLabel}-btn`}
        >
          <Trash2 className="w-4 h-4 mr-1.5" />
          Delete selected
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <ShieldAlert className="w-5 h-5" />
              Permanently delete {selectedIds.length} {entityLabel}
              {selectedIds.length > 1 ? 's' : ''}?
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              This action cannot be undone. Linked ledger and payment entries
              will also be cleaned up.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {supportModes && (
              <div className="space-y-1">
                <Label className="text-sm font-medium">Cascade mode</Label>
                <RadioGroup value={mode} onValueChange={setMode} className="space-y-2">
                  <label className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer ${mode === 'soft' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}>
                    <RadioGroupItem value="soft" id="mode-soft" className="mt-0.5" data-testid="mode-soft" />
                    <div className="text-sm">
                      <div className="font-medium text-gray-800">Soft — keep received stock</div>
                      <div className="text-xs text-gray-500">Deletes PO + ledger/payment entries. Approved GRNs and the stock they created remain.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer ${mode === 'hard' ? 'bg-rose-50 border-rose-200' : 'bg-white border-gray-200'}`}>
                    <RadioGroupItem value="hard" id="mode-hard" className="mt-0.5" data-testid="mode-hard" />
                    <div className="text-sm">
                      <div className="font-medium text-rose-800">Hard — reverse stock too</div>
                      <div className="text-xs text-rose-600">Deletes every linked GRN and reverses the stock they produced.</div>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-sm">Re-enter your password</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your admin password"
                  className="pr-10"
                  data-testid="bulk-delete-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              Deleted data is permanent. Only HQ Super Admin can perform this.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={submit}
              disabled={busy || !password}
              data-testid="bulk-delete-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete {selectedIds.length} {entityLabel}{selectedIds.length > 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
